import * as _ from 'lodash-es';
import { Parchment } from 'quill/core';
import QuillVideo from 'quill/formats/video';
import React from 'react';
import { createPortal } from 'react-dom';
import { IS_IOS } from '../../constants/os';
import safeParseUrl from '../../utils/url/safeParseUrl';
import { LINK_TYPE_ATTRIBUTE, LINK_TYPES } from '../constants';
import Comment from './Comment';
import Diff from './Diff';
import Reaction from './Reaction';
import Scroll from './Scroll';
import Span from './Span';
import VersionChange from './VersionChange';
import * as styles from './Video.module.scss';
import ignoreInChromatic from '../../utils/ignoreInChromatic';
import { needRenderAsIFrame } from '../utils/matchVideo';

// for iOS, we need to append a media fragment to the URL to force the browser to
// load the first frame of the video. Otherwise, the video will not load until
// the user interacts with the video (e.g. by tapping on it).
// See https://muffinman.io/blog/hack-for-ios-safari-to-display-html-video-thumbnail/
function appendFirstFrameMediaFragmentIfNeeded(urlString: string) {
  const url = safeParseUrl(urlString);

  if (url == null || !IS_IOS) return urlString;

  const hashParams = new URLSearchParams(url.hash.replace('#', ''));

  if (hashParams.has('t')) {
    return urlString;
  }

  hashParams.set('t', '0.001');
  url.hash = hashParams.toString();

  return url.toString();
}

class Video extends QuillVideo {
  static shouldLazyLoad = true;

  static create(url: string) {
    const node = document.createElement('div');
    node.classList.add(Video.className);
    node.setAttribute('data-url', url);
    node.setAttribute('contenteditable', 'false');
    node.classList.add(styles.container);
    return node;
  }

  static value(domNode: Element) {
    return domNode.getAttribute('data-url');
  }

  constructor(scroll: Parchment.ScrollBlot, domNode: Element) {
    super(scroll, domNode);
    // @ts-expect-error
    this.id = _.uniqueId();
  }

  attach() {
    super.attach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_MOUNT, this);
  }

  detach() {
    super.detach();
    // @ts-expect-error
    this.scroll.emitter.emit(Scroll.events.BLOT_UNMOUNT, this);
  }

  html() {
    const { video } = this.value();
    return `<a ${LINK_TYPE_ATTRIBUTE}="${LINK_TYPES.embed}" href="${video}">${video}</a>`;
  }

  format(name: string, value: string) {
    if (
      name === Comment.attrName ||
      name === Reaction.attrName ||
      name === Span.attrName ||
      name === Diff.attrName ||
      name === VersionChange.attrName
    ) {
      const attributor = this.scroll.query(name, Parchment.Scope.ATTRIBUTE);
      if (attributor instanceof Parchment.ClassAttributor) {
        this.attributes.attribute(attributor, value);
      }
    } else {
      super.format(name, value);
    }
  }

  formats() {
    return this.attributes.values();
  }

  getReferenceNodeForBounds() {
    return (
      this.domNode.querySelector('.video-content') ??
      this.domNode.querySelector('video, iframe') ??
      this.domNode
    );
  }

  renderPortal() {
    const url = Video.sanitize(this.value().video);
    return createPortal(
      <div className="video-content">
        {needRenderAsIFrame(url) ? (
          // eslint-disable-next-line jsx-a11y/iframe-has-title
          <iframe
            {...ignoreInChromatic()}
            frameBorder={0}
            allowFullScreen
            src={url}
          />
        ) : (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            {...ignoreInChromatic()}
            src={appendFirstFrameMediaFragmentIfNeeded(url)}
            onMouseOver={(e) => {
              (e.target as HTMLElement).setAttribute('controls', 'true');
            }}
            onMouseOut={(e) => {
              (e.target as HTMLElement).removeAttribute('controls');
            }}
            aria-label="Embedded video"
          />
        )}
      </div>,
      this.domNode,
    );
  }
}

Video.tagName = 'div';

export default Video;
